import React2, { createContext, useMemo, useContext } from 'react';

// src/modules/registry.ts
var PAID_MODULES = ["pos", "invoicing", "activities", "inspections"];
var MODULE_CATALOG = {
  pos: {
    key: "pos",
    label: "Kassa (POS)",
    description: "Cafeteria till per site: catalog, sales, receipts, daily close, cash counting, reporting and export. Works standalone; forwards on-account sales to invoicing when that module is also active."
  },
  invoicing: {
    key: "invoicing",
    label: "Facturatie",
    description: "Resident invoicing: billable-item ledger, invoices and credit notes with the organisation's own issuer identity and numbering, payments and reconciliation. Works standalone through manual item entry."
  },
  // 20260825-animatie-activiteiten-en-agenda (LD-1).
  //
  // ⚠ THE AGENDA ITSELF IS NOT IN THIS MODULE. Three layers, three questions:
  //
  //   tablet agenda visible     ← building feature flag `agenda` (default TRUE)
  //     └─ activities + signup  ← THIS module (per company, default OFF)
  //          └─ with a price    ← + `invoicing` AND a unit billing account
  //
  // Gating the base agenda behind this key would have been the obvious shortcut
  // and a fleet-wide regression: a paid module resolves to OFF with no
  // company_module row (D31), so every existing customer's tablets would have lost
  // their agenda on release day.
  activities: {
    key: "activities",
    label: "Animatie & activiteiten",
    description: "Activiteiten met inschrijving per datum: reeksen en herhaling, capaciteit en wachtlijst, inschrijfdeadlines, deelnemers per appartement en een aanwezigheidslijst per activiteit. Werkt zelfstandig; met een prijs erbij boekt hij door naar facturatie wanneer die module ook actief is."
  },
  // 20260901-bewoner-onboarding/plaatsbeschrijving.md (LD-13).
  //
  // ⚠ THE NAME COLLIDES, AND THE DESCRIPTION IS WHERE THAT IS SETTLED.
  //
  // In a platform whose core domain is a device fleet, "inspection" already means someone
  // going to look at hardware: `type_audit` ("Audit / inspectie") is a user-visible ticket
  // type, ButtonTest is a device inspection in substance, and FieldInterventionReport is
  // the report of an engineer's site visit. A different KEY could never have carried that
  // distinction — but MODULE_CATALOG.description is "what the customer is actually buying"
  // and is shown in the toggle confirmation, so it is the place to draw the line. An admin
  // reads its last sentence and cannot mistake the two.
  //
  // Key English like every other key, label Dutch because a human reads it — the same
  // pattern as pos → "Kassa (POS)" and invoicing → "Facturatie".
  //
  // Nothing this module gates exists today (no condition report, no meter reading, no
  // apartment floor plan anywhere in either repo), so gating all of it removes nothing
  // from anyone and there is no release-day regression to design around — unlike
  // `activities`, which had to argue the base agenda out of its own scope.
  inspections: {
    key: "inspections",
    label: "Plaatsbeschrijvingen",
    description: "Plaatsbeschrijvingen van appartementen bij intrede en uittrede: ruimte-per-ruimte checklist met foto's, meterstanden, grondplan met aanduidingen, handtekeningen en PDF. Dit gaat over de staat van een woning \u2014 niet over de inspectie of controle van toestellen."
  }
};
function isModuleKey(value) {
  return typeof value === "string" && PAID_MODULES.includes(value);
}

// src/modules/resolve.ts
function closedState() {
  return { enabled: false, everEnabled: false, expired: false, validUntil: null };
}
function toDate(value) {
  if (value === null || value === void 0) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
function toBool(value) {
  if (value === true) return true;
  if (value === false || value === null || value === void 0) return false;
  return Number(value) === 1;
}
function resolveModules(rows, now = /* @__PURE__ */ new Date()) {
  const out = {};
  for (const key of PAID_MODULES) out[key] = closedState();
  if (!Array.isArray(rows)) return out;
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const key = row.module;
    if (!(key in out)) continue;
    const validUntil = toDate(row.valid_until);
    const everEnabled = toDate(row.enabled_at) !== null;
    const flagged = toBool(row.enabled);
    const expired = flagged && validUntil !== null && validUntil.getTime() <= now.getTime();
    out[key] = {
      enabled: flagged && !expired,
      // A row that is enabled right now implies it has been enabled, even if a
      // caller handed us a row with no enabled_at.
      everEnabled: everEnabled || flagged && !expired,
      expired,
      validUntil
    };
  }
  return out;
}
function isModuleEnabled(state, key) {
  return state?.[key]?.enabled === true;
}
function wasModuleEverEnabled(state, key) {
  return state?.[key]?.everEnabled === true;
}
function closedModules() {
  const out = {};
  for (const key of PAID_MODULES) out[key] = closedState();
  return out;
}
var ModulesContext = createContext(null);
function ModuleProvider({ rows, now, children }) {
  const value = useMemo(() => {
    const loading = rows === null || rows === void 0;
    const modules = loading ? closedModules() : resolveModules(rows, now);
    return {
      modules,
      loading,
      canWrite: (key) => modules[key]?.enabled === true,
      canRead: (key) => modules[key]?.everEnabled === true
    };
  }, [rows, now]);
  return React2.createElement(ModulesContext.Provider, { value }, children);
}
function useModules() {
  const ctx = useContext(ModulesContext);
  if (ctx) return ctx;
  const modules = closedModules();
  return {
    modules,
    loading: true,
    canWrite: () => false,
    canRead: () => false
  };
}
function ModuleGate({
  module,
  mode = "write",
  children,
  fallback = null
}) {
  const { canWrite, canRead } = useModules();
  const allowed = mode === "read" ? canRead(module) : canWrite(module);
  if (!allowed) {
    return fallback ?? null;
  }
  return React2.createElement(React2.Fragment, null, children);
}

export { MODULE_CATALOG, ModuleGate, ModuleProvider, PAID_MODULES, closedModules, isModuleEnabled, isModuleKey, resolveModules, useModules, wasModuleEverEnabled };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map